import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
    Check,
    FolderPlus,
    GripVertical,
    ListOrdered,
    Pencil,
    RotateCcw,
    Save,
    Trash2,
    X,
} from 'lucide-react'
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCorners,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type {
    CollisionDetection,
    DragEndEvent,
    DragOverEvent,
} from '@dnd-kit/core'
import type { LucideIcon } from 'lucide-react'

import type {
    ArrangedChild,
    ArrangedNode,
} from '@/components/sidebar/nav-layout'
import type { SidebarLayoutNode } from '@/lib/api/sidebar-layout-api'
import { arrangeSidebar } from '@/components/sidebar/nav-layout'
import {
    CHILD,
    GROUP,
    TOP,
    dragGhostLabel,
    isSameDragLevel,
    parseDndId,
    topKeyOf,
} from '@/components/sidebar/nav-layout-dnd'
import { getNavItems } from '@/components/sidebar/nav-items'
import {
    useSidebarLayout,
    useUpdateSidebarLayout,
} from '@/hooks/use-sidebar-layout-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/settings/sidebar-layout',
)({
    component: SidebarLayoutPage,
})

// id namespaces + level rules live in `nav-layout-dnd.ts` so they are unit
// tested; see TOP/GROUP/CHILD, parseDndId, isSameDragLevel, topKeyOf.

interface CodeMeta {
    label: string
    icon: LucideIcon
}

/** Two separate lookups so a group's basePath never collides with a child's
 * `to` (e.g. group `/customers` vs its first child `/customers`). `top` keys
 * top-level entries (groups by basePath, links by to); `child` keys children. */
interface CodeMetaMaps {
    top: Map<string, CodeMeta>
    child: Map<string, CodeMeta>
}

function buildCodeMeta(): CodeMetaMaps {
    const top = new Map<string, CodeMeta>()
    const child = new Map<string, CodeMeta>()
    for (const e of getNavItems()) {
        if ('items' in e) {
            top.set(e.basePath, { label: e.label, icon: e.icon })
            for (const c of e.items)
                child.set(c.to, { label: c.label, icon: c.icon })
        } else {
            top.set(e.to, { label: e.label, icon: e.icon })
        }
    }
    return { top, child }
}

/** Serialize the editable tree back to the persisted shape (drop empty
 * overrides so defaults come from code). */
function toLayout(nodes: ArrangedNode[]): SidebarLayoutNode[] {
    return nodes.map((n) => {
        if (n.kind === 'link') return { key: n.key, kind: 'link' }
        const node: SidebarLayoutNode = { key: n.key, kind: 'group' }
        if (n.custom) node.custom = true
        if (n.overrideLabel) node.label = n.overrideLabel
        node.children = n.children.map((c) =>
            c.overrideLabel
                ? { key: c.key, label: c.overrideLabel }
                : { key: c.key },
        )
        return node
    })
}

function SidebarLayoutPage() {
    const navEntries = useMemo(() => getNavItems(), [])
    const codeMeta = useMemo(() => buildCodeMeta(), [])
    const { data: saved, isLoading } = useSidebarLayout()
    const update = useUpdateSidebarLayout()

    const [nodes, setNodes] = useState<ArrangedNode[]>([])
    // Id of the row being dragged — drives the DragOverlay ghost.
    const [activeId, setActiveId] = useState<string | null>(null)
    const initialized = useRef(false)
    // Internal scroll container of the menu list + a flag to auto-scroll to the
    // bottom right after a new parent is appended.
    const listRef = useRef<HTMLDivElement>(null)
    const scrollToBottomPending = useRef(false)

    useEffect(() => {
        if (initialized.current || isLoading) return
        setNodes(arrangeSidebar(navEntries, saved?.layout))
        initialized.current = true
    }, [saved, isLoading, navEntries])

    useEffect(() => {
        if (!scrollToBottomPending.current || !listRef.current) return
        scrollToBottomPending.current = false
        listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: 'smooth',
        })
    }, [nodes])

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )

    // childKey -> owning group key (recomputed each render from state).
    const childToGroup = useMemo(() => {
        const map = new Map<string, string>()
        for (const n of nodes) {
            if (n.kind === 'group') {
                for (const c of n.children) map.set(c.key, n.key)
            }
        }
        return map
    }, [nodes])

    const containerOf = (id: string): string | undefined => {
        const p = parseDndId(id)
        if (!p) return undefined
        if (p.type === 'child') return childToGroup.get(p.key)
        if (p.type === 'group') return p.key
        // top: only a group can host children
        const n = nodes.find((x) => x.key === p.key)
        return n && n.kind === 'group' ? p.key : undefined
    }

    // Restrict drop candidates to the dragged item's own level. A group's
    // sortable rect wraps its children list, so an unfiltered closestCorners
    // hands back a child:/group: id the moment a dragged top-level entry passes
    // over an expanded group — and the drop is discarded. Filtering makes the
    // nearest target that group's top: id instead.
    const collisionDetection: CollisionDetection = (args) =>
        closestCorners({
            ...args,
            droppableContainers: args.droppableContainers.filter((c) =>
                isSameDragLevel(String(args.active.id), String(c.id)),
            ),
        })

    const handleDragOver = (e: DragOverEvent) => {
        const active = parseDndId(String(e.active.id))
        if (!active || active.type !== 'child' || !e.over) return
        const from = childToGroup.get(active.key)
        const to = containerOf(String(e.over.id))
        if (!from || !to || from === to) return
        setNodes((prev) =>
            moveChildBetween(prev, active.key, from, to, String(e.over?.id)),
        )
    }

    // Label + icon of the ghost rendered inside DragOverlay. Groups show only
    // their header row — a full card copy would duplicate the children list.
    const ghost = useMemo(() => {
        const label = dragGhostLabel(activeId, nodes, (type, key) =>
            type === 'child'
                ? codeMeta.child.get(key)?.label
                : codeMeta.top.get(key)?.label,
        )
        if (label === null) return null
        const parsed = parseDndId(activeId as string)
        const meta =
            parsed?.type === 'child'
                ? codeMeta.child.get(parsed.key)
                : parsed && codeMeta.top.get(parsed.key)
        return { label, Icon: meta?.icon ?? FolderPlus }
    }, [activeId, nodes, codeMeta])

    const handleDragEnd = (e: DragEndEvent) => {
        setActiveId(null)
        const active = parseDndId(String(e.active.id))
        if (!active || !e.over) return
        const overId = String(e.over.id)

        if (active.type === 'top') {
            // Resolve to the owning top-level entry so the reorder still lands
            // if `over` is a child/group id (keyboard sensor, or a future
            // collision strategy that does not filter by level).
            const overKey = topKeyOf(overId, childToGroup)
            if (!overKey || active.key === overKey) return
            setNodes((prev) => {
                const from = prev.findIndex((n) => n.key === active.key)
                const to = prev.findIndex((n) => n.key === overKey)
                if (from < 0 || to < 0) return prev
                return arrayMove(prev, from, to)
            })
            return
        }

        if (active.type === 'child') {
            const group = childToGroup.get(active.key)
            if (!group) return
            const over = parseDndId(overId)
            // Reorder within the (possibly newly-moved) container.
            if (
                over?.type === 'child' &&
                childToGroup.get(over.key) === group
            ) {
                setNodes((prev) =>
                    prev.map((n) => {
                        if (n.kind !== 'group' || n.key !== group) return n
                        const from = n.children.findIndex(
                            (c) => c.key === active.key,
                        )
                        const to = n.children.findIndex(
                            (c) => c.key === over.key,
                        )
                        if (from < 0 || to < 0) return n
                        return {
                            ...n,
                            children: arrayMove(n.children, from, to),
                        }
                    }),
                )
            }
        }
    }

    const setGroupLabel = (key: string, label: string | undefined) =>
        setNodes((prev) =>
            prev.map((n) =>
                n.key === key ? { ...n, overrideLabel: label } : n,
            ),
        )

    const setChildLabel = (
        groupKey: string,
        childKey: string,
        label: string | undefined,
    ) =>
        setNodes((prev) =>
            prev.map((n) => {
                if (n.kind !== 'group' || n.key !== groupKey) return n
                return {
                    ...n,
                    children: n.children.map((c) =>
                        c.key === childKey ? { ...c, overrideLabel: label } : c,
                    ),
                }
            }),
        )

    const addCustomParent = () => {
        const id = crypto.randomUUID().slice(0, 8)
        scrollToBottomPending.current = true
        setNodes((prev) => [
            ...prev,
            {
                key: `custom:${id}`,
                kind: 'group',
                custom: true,
                overrideLabel: m.sidebar_layout_new_parent_name(),
                children: [],
            },
        ])
    }

    const removeCustomParent = (key: string) =>
        setNodes((prev) => prev.filter((n) => n.key !== key))

    const handleSave = () => update.mutate(toLayout(nodes))
    const handleReset = () => {
        if (!window.confirm(m.sidebar_layout_reset_confirm())) return
        setNodes(arrangeSidebar(navEntries, []))
        update.mutate([])
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-4 px-1 py-2 md:p-6">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <ListOrdered className="size-6 text-primary" />
                    <h1 className="text-2xl font-semibold">
                        {m.sidebar_layout_title()}
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={addCustomParent}
                        disabled={update.isPending}
                    >
                        <FolderPlus className="size-4 mr-1" />
                        {m.sidebar_layout_add_parent()}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                        disabled={update.isPending}
                    >
                        <RotateCcw className="size-4 mr-1" />
                        {m.sidebar_layout_reset()}
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={update.isPending || nodes.length === 0}
                    >
                        <Save className="size-4 mr-1" />
                        {m.sidebar_layout_save()}
                    </Button>
                </div>
            </div>
            <p className="shrink-0 text-sm text-muted-foreground">
                {m.sidebar_layout_subtitle_v2()}
            </p>

            <Card className="flex min-h-0 flex-1 flex-col p-0">
                <CardHeader className="shrink-0 p-4 pb-3">
                    <CardTitle className="text-base">
                        {m.sidebar_layout_top_level()}
                    </CardTitle>
                </CardHeader>
                <CardContent
                    ref={listRef}
                    className="min-h-0 flex-1 overflow-y-auto px-4 pb-4"
                >
                    {isLoading ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={collisionDetection}
                            onDragStart={(e) =>
                                setActiveId(String(e.active.id))
                            }
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            onDragCancel={() => setActiveId(null)}
                        >
                            <SortableContext
                                items={nodes.map((n) => `${TOP}${n.key}`)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="space-y-1.5">
                                    {nodes.map((node) => (
                                        <TopNode
                                            key={node.key}
                                            node={node}
                                            codeMeta={codeMeta}
                                            onSetGroupLabel={setGroupLabel}
                                            onSetChildLabel={setChildLabel}
                                            onRemove={removeCustomParent}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                            <DragOverlay dropAnimation={null}>
                                {ghost && (
                                    <div className="flex items-center gap-2 rounded-md border bg-card px-2 py-2 shadow-lg">
                                        <GripVertical className="size-4 shrink-0 text-muted-foreground" />
                                        <ghost.Icon className="size-4 shrink-0 text-muted-foreground" />
                                        <span className="truncate text-sm font-medium">
                                            {ghost.label}
                                        </span>
                                    </div>
                                )}
                            </DragOverlay>
                        </DndContext>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

/** Move a child from one group to another, inserting near the `over` target. */
function moveChildBetween(
    nodes: ArrangedNode[],
    childKey: string,
    fromKey: string,
    toKey: string,
    overId: string,
): ArrangedNode[] {
    let moved: ArrangedChild | undefined
    const stripped = nodes.map((n) => {
        if (n.kind !== 'group' || n.key !== fromKey) return n
        const child = n.children.find((c) => c.key === childKey)
        if (child) moved = child
        return { ...n, children: n.children.filter((c) => c.key !== childKey) }
    })
    if (!moved) return nodes
    const over = parseDndId(overId)
    return stripped.map((n) => {
        if (n.kind !== 'group' || n.key !== toKey) return n
        let index = n.children.length
        if (over?.type === 'child') {
            const i = n.children.findIndex((c) => c.key === over.key)
            if (i >= 0) index = i
        }
        const next = [...n.children]
        next.splice(index, 0, moved as ArrangedChild)
        return { ...n, children: next }
    })
}

interface TopNodeProps {
    node: ArrangedNode
    codeMeta: CodeMetaMaps
    onSetGroupLabel: (key: string, label: string | undefined) => void
    onSetChildLabel: (
        groupKey: string,
        childKey: string,
        label: string | undefined,
    ) => void
    onRemove: (key: string) => void
}

function TopNode({
    node,
    codeMeta,
    onSetGroupLabel,
    onSetChildLabel,
    onRemove,
}: TopNodeProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: `${TOP}${node.key}` })
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        // The DragOverlay ghost is the visible copy while dragging; keeping the
        // real row in the flow but invisible preserves its slot without letting
        // neighbours translate over it (top-level heights differ a lot).
        opacity: isDragging ? 0 : 1,
    }
    const meta = codeMeta.top.get(node.key)
    const Icon = meta?.icon ?? FolderPlus
    const defaultLabel = meta?.label ?? node.key

    if (node.kind === 'link') {
        return (
            <div
                ref={setNodeRef}
                style={style}
                className="flex items-center gap-2 rounded-md border bg-card px-2 py-2"
            >
                <DragHandle attributes={attributes} listeners={listeners} />
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-sm font-medium">
                    {defaultLabel}
                </span>
                <span className="text-[11px] text-muted-foreground">
                    {m.sidebar_layout_badge_link()}
                </span>
            </div>
        )
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="rounded-md border bg-card"
        >
            <div className="flex items-center gap-2 px-2 py-2">
                <DragHandle attributes={attributes} listeners={listeners} />
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <EditableLabel
                    value={node.overrideLabel}
                    placeholder={defaultLabel}
                    onChange={(label) => onSetGroupLabel(node.key, label)}
                />
                {node.custom && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                        {m.sidebar_layout_badge_custom()}
                    </span>
                )}
                {node.custom && node.children.length === 0 && (
                    <button
                        type="button"
                        onClick={() => onRemove(node.key)}
                        title={m.sidebar_layout_delete_parent()}
                        className="rounded p-1 text-destructive hover:bg-destructive/10"
                    >
                        <Trash2 className="size-4" />
                    </button>
                )}
            </div>
            <GroupChildren
                node={node}
                codeMeta={codeMeta}
                onSetChildLabel={onSetChildLabel}
            />
        </div>
    )
}

function GroupChildren({
    node,
    codeMeta,
    onSetChildLabel,
}: {
    node: ArrangedNode
    codeMeta: CodeMetaMaps
    onSetChildLabel: (
        groupKey: string,
        childKey: string,
        label: string | undefined,
    ) => void
}) {
    const { setNodeRef, isOver } = useDroppable({ id: `${GROUP}${node.key}` })
    return (
        <div
            ref={setNodeRef}
            className={`border-t px-2 py-2 pl-6 ${isOver ? 'bg-primary/5' : ''}`}
        >
            <SortableContext
                items={node.children.map((c) => `${CHILD}${c.key}`)}
                strategy={verticalListSortingStrategy}
            >
                {node.children.length === 0 ? (
                    <p className="py-2 text-center text-xs text-muted-foreground">
                        {m.sidebar_layout_drop_here()}
                    </p>
                ) : (
                    <div className="space-y-1">
                        {node.children.map((child) => (
                            <ChildRow
                                key={child.key}
                                groupKey={node.key}
                                child={child}
                                codeMeta={codeMeta}
                                onSetChildLabel={onSetChildLabel}
                            />
                        ))}
                    </div>
                )}
            </SortableContext>
        </div>
    )
}

function ChildRow({
    groupKey,
    child,
    codeMeta,
    onSetChildLabel,
}: {
    groupKey: string
    child: ArrangedChild
    codeMeta: CodeMetaMaps
    onSetChildLabel: (
        groupKey: string,
        childKey: string,
        label: string | undefined,
    ) => void
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: `${CHILD}${child.key}` })
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        // The DragOverlay ghost is the visible copy while dragging; keeping the
        // real row in the flow but invisible preserves its slot without letting
        // neighbours translate over it (top-level heights differ a lot).
        opacity: isDragging ? 0 : 1,
    }
    const meta = codeMeta.child.get(child.key)
    const Icon = meta?.icon
    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5"
        >
            <DragHandle attributes={attributes} listeners={listeners} />
            {Icon && (
                <Icon className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <EditableLabel
                value={child.overrideLabel}
                placeholder={meta?.label ?? child.key}
                onChange={(label) =>
                    onSetChildLabel(groupKey, child.key, label)
                }
                small
            />
        </div>
    )
}

/** Inline rename: click pencil to edit. Blur or Enter auto-saves; a blur with an
 * empty (trimmed) input KEEPS the current content unchanged. Escape / ✗ cancels. */
function EditableLabel({
    value,
    placeholder,
    onChange,
    small,
}: {
    value: string | undefined
    placeholder: string
    onChange: (label: string | undefined) => void
    small?: boolean
}) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(value ?? '')

    const start = () => {
        setDraft(value ?? '')
        setEditing(true)
    }
    const save = () => {
        const trimmed = draft.trim()
        // Non-empty → save the new name. Empty → keep current content (no change).
        if (trimmed.length > 0) onChange(trimmed)
        setEditing(false)
    }
    const cancel = () => setEditing(false)

    if (editing) {
        return (
            <span className="flex flex-1 items-center gap-1">
                <Input
                    autoFocus
                    value={draft}
                    placeholder={placeholder}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={save}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') save()
                        if (e.key === 'Escape') cancel()
                    }}
                    className="h-7 flex-1"
                    maxLength={60}
                />
                {/* preventDefault on mousedown keeps input focus so clicking a
                    button doesn't trigger the blur-save before the click. */}
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={save}
                    className="rounded p-1 text-primary hover:bg-muted"
                    title={m.common_save()}
                >
                    <Check className="size-4" />
                </button>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={cancel}
                    className="rounded p-1 text-muted-foreground hover:bg-muted"
                    title={m.common_cancel()}
                >
                    <X className="size-4" />
                </button>
            </span>
        )
    }

    return (
        <span className="flex flex-1 items-center gap-1.5">
            <span
                className={`flex-1 truncate ${small ? 'text-sm' : 'text-sm font-medium'}`}
            >
                {value ?? placeholder}
            </span>
            {value && (
                <span className="text-[11px] text-muted-foreground">
                    {m.sidebar_layout_renamed()}
                </span>
            )}
            <button
                type="button"
                onClick={start}
                className="rounded p-1 text-muted-foreground hover:bg-muted"
                title={m.sidebar_layout_rename()}
            >
                <Pencil className="size-3.5" />
            </button>
        </span>
    )
}

function DragHandle({
    attributes,
    listeners,
}: {
    attributes: ReturnType<typeof useSortable>['attributes']
    listeners: ReturnType<typeof useSortable>['listeners']
}) {
    return (
        <button
            type="button"
            className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
            aria-label={m.sidebar_layout_drag_hint()}
            {...attributes}
            {...listeners}
        >
            <GripVertical className="size-4" />
        </button>
    )
}
